import { useState, useMemo, useEffect } from 'react'
import {
    ColumnDef,
    SortingState,
    VisibilityState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table"
import { Button } from '@/components/ui/button'
import { RefreshCw } from 'lucide-react'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { downloadCSV, erpLogsMapper, displayDateTime, formatToPKR, generateQueryString } from '@/lib/helper'
import { useToast } from '@/components/ui/use-toast'
import Pagination from '@/components/ui/table/pagination-table'
import TableSearchFilter from '@/components/ui/table/data-table-search'
import usePagination from '@/hooks/use-pagination'
import { DataTableColumnHeader } from '@/components/ui/table/data-table-column-header'
import useTableData from '@/components/ui/table/data-table-logic'
import { DataTableRowActions } from './data-table-row-action'
import { FaFileCsv } from 'react-icons/fa'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import TableFilters from '@/components/ui/table/data-table-filter'
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip"
import { useGetBudgetLogsQuery, useLazyGetBudgetLogsQuery } from '@/services/dhAccountingApi'
import { BudgetLog } from '@/types/budget'

// API response type (matches the response structure)
export type ERPLogEntry = BudgetLog


interface SearchCriteriaType extends Record<string, string | number | (string | number)[] | null | undefined> {
    limit: number
    offset: number
    sort_order: string
    sort_key: string
    search_key?: string
    statuses?: (string | number)[]
    date_from?: string
    date_to?: string
    amount_min?: number
    amount_max?: number
    type: string
}

const initialValue: SearchCriteriaType = {
    limit: 10,
    offset: 0,
    sort_order: 'desc',
    sort_key: 'id',
    type: 'activity',
    search_key: '',
}

const getStatusBadge = (status: string) => {
    const statusLower = status.toLowerCase()
    const variants: Record<string, string> = {
        posted: 'bg-green-100 text-green-800 border-green-200',
        failed: 'bg-red-100 text-red-800 border-red-200',
        processing: 'bg-yellow-100 text-yellow-800 border-yellow-200'
    }

    const variantClass = variants[statusLower] || 'bg-gray-100 text-gray-800 border-gray-200'

    return (
        <Badge variant="outline" className={cn('font-medium', variantClass)}>
            {status}
        </Badge>
    )
}


export const ERPLogsTable = () => {
    const { toast } = useToast()
    const {
        searchCriteria,
        toggleSortOrder,
        setSearchCriteria,
        inputValue,
        setInputValue,
    } = useTableData(initialValue)

    const [sorting, setSorting] = useState<SortingState>([])
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
    const [rowSelection, setRowSelection] = useState({})
    const [selectAllRows, setSelectAllRows] = useState(false)
    const [showSelectAllNotification, setShowSelectAllNotification] = useState(false)
    const [selectedLog, setSelectedLog] = useState<ERPLogEntry | null>(null)
    const [showDetailsDialog, setShowDetailsDialog] = useState(false)
    const [openFilter, setOpenFilter] = useState(false)
    const [filterData, setFilter] = useState<Record<string, string | number | (string | number)[]>>({})

    // Build query string for API
    const apiQuery = useMemo(() => {
        const queryParams = { ...searchCriteria, ...filterData }
        return generateQueryString(queryParams)
    }, [searchCriteria])

    // Fetch data from API
    const { data: apiResponse, isLoading, isError } = useGetBudgetLogsQuery(apiQuery+"&load_summary=true")
    
    // Lazy query for exporting all data
    const [getBudgetLogsForExport, { isLoading: isExporting }] = useLazyGetBudgetLogsQuery()

    // Extract data from API response
    const tableData = useMemo(() => {
        return apiResponse?.data || []
    }, [apiResponse])

    const totalRecords = apiResponse?.filtered_counts || 0

    // Calculate summary statistics from all filtered data
    const summaryStats = useMemo(() => {
        const totalLogs = apiResponse?.summary?.total_logs || 0
        const postedLogs = apiResponse?.summary?.posted_logs || 0
        const processingLogs = apiResponse?.summary?.processing_logs || 0
        const failedLogs = apiResponse?.summary?.failed_logs || 0
        const totalAmount = apiResponse?.summary?.total_amount || 0
        const successRate = apiResponse?.summary?.success_rate || 0

        return {
            totalLogs,
            postedLogs,
            processingLogs,
            failedLogs,
            totalAmount,
            successRate,
        }
    }, [apiResponse])

    // Handle retry sync
    const handleRetrySync = (log: ERPLogEntry) => {
        // TODO: Implement actual API call to retry sync
        toast({
            variant: 'default',
            title: 'Retry Initiated',
            description: `Retrying sync for transaction ${log.transaction_number}...`,
        })

        // Simulate API call
        setTimeout(() => {
            toast({
                variant: 'success',
                title: 'Sync Retry Successful',
                description: `Transaction ${log.transaction_number} has been queued for retry.`,
            })
        }, 1500)
    }

    // Handle view details
    const handleViewDetails = (log: ERPLogEntry) => {
        setSelectedLog(log)
        setShowDetailsDialog(true)
    }

    // Handle filter changes
    function handleFilterChange(field: string, value: string | number | (string | number)[]) {
        setFilter((prev) => {
            const next = { ...prev }
            const isEmpty = Array.isArray(value) ? value.length === 0 : value === '' || value === null || value === undefined
            if (isEmpty) {
                delete next[field]
            } else {
                next[field] = value
            }
            return next
        })
    }

    // Filter configuration
    const filterDisplay = [
        {
            type: "multiselect",
            name: "statuses",
            label: "Status",
            options: [
                { label: "Posted", value: "posted" },
                { label: "Processing", value: "processing" },
                { label: "Failed", value: "failed" },
            ],
            function: (value: (string | number)[]) => handleFilterChange("statuses", value),
        },
    ]

    // Define columns
    const columns: ColumnDef<ERPLogEntry>[] = [
        {
            id: "select",
            header: ({ table }) => (
                <div className="flex items-center gap-2">
                    <Checkbox
                        checked={table.getIsAllPageRowsSelected()}
                        onCheckedChange={(value: any) =>
                            table.toggleAllPageRowsSelected(!!value)
                        }
                        aria-label="Select all"
                        className="translate-y-[2px]"
                    />
                </div>
            ),
            cell: ({ row }) => (
                <Checkbox
                    checked={row.getIsSelected()}
                    onCheckedChange={(value) => row.toggleSelected(!!value)}
                    aria-label="Select row"
                    className="translate-y-[2px]"
                />
            ),
            enableSorting: false,
            enableHiding: false,
        },
        {
            accessorKey: "transaction_tax_number",
            meta: "Tax No.",
            header: ({ column }) => (
                <DataTableColumnHeader
                    column={column}
                    title="Tax No."
                    sortKey="transaction_tax_number"
                    sortable={false}
                    searchCriteria={searchCriteria}
                    toggleSortOrder={toggleSortOrder}
                />
            ),
            cell: ({ row }) => (
                <div className="font-medium">{row.original.transaction_tax_number || '-'}</div>
            ),
        },
        {
            accessorKey: "voucher_no",
            meta: "Voucher No",
            header: ({ column }) => (
                <DataTableColumnHeader
                    column={column}
                    title="Voucher No."
                    sortKey="voucher_no"
                    sortable={false}
                    searchCriteria={searchCriteria}
                    toggleSortOrder={toggleSortOrder}
                />
            ),
            cell: ({ row }) => (
                <div className="font-medium">{row.original.voucher_no}</div>
            ),
        },
        {
            accessorKey: "bucket_no",
            meta: "Bucket No",
            header: ({ column }) => (
                <DataTableColumnHeader
                    column={column}
                    title="Bucket No."
                    sortKey="bucket_no"
                    sortable={false}
                    searchCriteria={searchCriteria}
                    toggleSortOrder={toggleSortOrder}
                />
            ),
            cell: ({ row }) => (
                <div className="font-medium">{row.original.bucket_no}</div>
            ),
        },
        {
            accessorKey: "amount",
            meta: "Amount",
            header: ({ column }) => (
                <DataTableColumnHeader
                    column={column}
                    title="Amount"
                    sortKey="amount"
                    className="flex justify-end"
                    searchCriteria={searchCriteria as any}
                    toggleSortOrder={toggleSortOrder}
                />
            ),
            cell: ({ row }) => (
                <div className="text-right font-medium">
                    {formatToPKR(row.original.amount)}
                </div>
            ),
        },
        {
            accessorKey: "status",
            meta: "Status",
            header: ({ column }) => (
                <DataTableColumnHeader
                    column={column}
                    title="Status"
                    sortKey="status"
                    searchCriteria={searchCriteria as any}
                    toggleSortOrder={toggleSortOrder}
                />
            ),
            cell: ({ row }) => getStatusBadge(row.original.status),
        },
        {
            accessorKey: "created_at",
            meta: "Created Date",
            header: ({ column }) => (
                <DataTableColumnHeader
                    column={column}
                    title="Created Date"
                    sortKey="created_at"
                    searchCriteria={searchCriteria as any}
                    toggleSortOrder={toggleSortOrder}
                />
            ),
            cell: ({ row }) => (
                <div className="text-sm text-gray-600">{displayDateTime(row.original.created_at)}</div>
            ),
        },
        {
            accessorKey: "message",
            meta: "Message",
            header: ({ column }) => (
                <DataTableColumnHeader
                    column={column}
                    title="Message"
                    sortKey="message"
                    sortable={false}
                    searchCriteria={searchCriteria as any}
                    toggleSortOrder={toggleSortOrder}
                />
            ),
            cell: ({ row }) => (
                <div className={cn(
                    "text-sm",
                    row.original.status.toLowerCase() === 'failed' ? "text-red-600" : "text-gray-600"
                )}>
                    {row.original.message || '-'}
                </div>
            ),
        },
        {
            id: "actions",
            header: "Actions",
            cell: ({ row }) => (
                <DataTableRowActions
                    log={row.original}
                    transactionId={row.original.transaction_id}
                    onRetry={handleRetrySync}
                    onViewDetails={handleViewDetails}
                />
            ),
            enableSorting: false,
            enableHiding: false,
        },
    ]

    // Initialize table
    const table = useReactTable({
        data: tableData,
        columns,
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        onColumnVisibilityChange: setColumnVisibility,
        onRowSelectionChange: setRowSelection,
        state: {
            sorting,
            columnVisibility,
            rowSelection,
        },
    })

    // Pagination handlers
    const {
        handleLimitChange,
        handleNextPage,
        handlePrevPage,
        handleFirstPage,
        handleLastPage,
        isLastPage,
    } = usePagination<SearchCriteriaType>({
        totalRecords,
        searchCriteria,
        setSearchCriteria,
    })

    // Handle select all rows
    const handleSelectAllRows = () => {
        setSelectAllRows(true)
        setShowSelectAllNotification(false)
    }

    // Handle clear selection
    const handleClearSelection = () => {
        setSelectAllRows(false)
        setShowSelectAllNotification(false)
        table.toggleAllPageRowsSelected(false)
        setRowSelection({})
    }

    // Handle showing select all notification
    useEffect(() => {
        const allPageRowsSelected = table.getIsAllPageRowsSelected()
        const selectedRowCount = table.getSelectedRowModel().rows.length
        const totalRowsInCurrentPage = table.getRowModel().rows.length

        if (allPageRowsSelected && selectedRowCount === totalRowsInCurrentPage && totalRowsInCurrentPage > 0 && !selectAllRows) {
            setShowSelectAllNotification(true)
        } else if (selectedRowCount === 0 || selectAllRows) {
            setShowSelectAllNotification(false)
        }
    }, [table.getIsAllPageRowsSelected(), table.getSelectedRowModel().rows.length, selectAllRows, table])

    // Reset select all when row selection changes
    useEffect(() => {
        if (Object.keys(rowSelection).length === 0) {
            setSelectAllRows(false)
            setShowSelectAllNotification(false)
        }
    }, [rowSelection])

    // Auto-select all visible rows when selectAllRows is true and data changes
    useEffect(() => {
        if (selectAllRows && tableData.length > 0) {
            table.toggleAllPageRowsSelected(true)
        }
    }, [selectAllRows, tableData, table])

    // Export functionality
    const handleExportSelected = async () => {
        if (selectAllRows) {
            // Export all filtered rows
            try {
                // Build export query with all filters but without pagination (no limit)
                // Include search_key from searchCriteria if present
                const exportQueryParams: Record<string, string | number | (string | number)[]> = {
                    sort_key: 'id',
                    sort_order: 'desc',
                    offset: 0,
                    type: 'activity',
                    ...filterData,
                }
                
                // Explicitly exclude limit from export query (user requirement: without limit)
                // Remove limit if it exists in filterData
                delete exportQueryParams.limit
                
                // Add search_key if present
                if (searchCriteria.search_key && searchCriteria.search_key.trim() !== '') {
                    exportQueryParams.search_key = searchCriteria.search_key
                }
                
                // Build query string
                const exportQuery = generateQueryString(exportQueryParams)
                
                // Fetch all data without summary (for faster export)
                const result = await getBudgetLogsForExport(exportQuery).unwrap()
                
                if (result?.data && result.data.length > 0) {
                    downloadCSV(result.data, 'erp_logs_all.csv', erpLogsMapper)
                    toast({
                        variant: 'success',
                        title: `Exported ${result.data.length} ERP logs successfully`,
                    })
                } else {
                    toast({
                        variant: 'destructive',
                        title: 'No data to export',
                        description: 'No ERP log records found to export.',
                    })
                }
            } catch (error) {
                console.error('Export failed:', error)
                toast({
                    variant: 'destructive',
                    title: 'Export failed',
                    description: 'Failed to export all ERP logs. Please try again.',
                })
            }
        } else {
            // Export selected rows
            const selectedRows = table.getSelectedRowModel().rows.map((row) => row.original)
            if (selectedRows.length === 0) {
                toast({
                    variant: 'destructive',
                    title: 'Please select one or more records to perform this action.',
                })
                return
            }
            downloadCSV(selectedRows, 'erp_logs_selected.csv', erpLogsMapper)
            toast({
                variant: 'success',
                title: `Exported ${selectedRows.length} ERP logs successfully`,
            })
        }
    }

    return (
        <div className="w-full space-y-4">
            {/* Summary Tiles */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 px-3">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                    <div className="flex items-center justify-between">
                        <div className="min-w-0">
                            <p className="text-xs sm:text-sm font-medium text-blue-600 truncate">Total Logs</p>
                            <p className="text-xl sm:text-2xl font-bold text-blue-800">{summaryStats.totalLogs}</p>
                        </div>
                        <div className="w-7 h-7 sm:w-8 sm:h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 ml-1">
                            <i className="fa fa-file-text text-blue-600 text-xs sm:text-sm"></i>
                        </div>
                    </div>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4">
                    <div className="flex items-center justify-between">
                        <div className="min-w-0">
                            <p className="text-xs sm:text-sm font-medium text-green-600 truncate">Success Rate</p>
                            <p className="text-xl sm:text-2xl font-bold text-green-800">{summaryStats.successRate}%</p>
                        </div>
                        <div className="w-7 h-7 sm:w-8 sm:h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 ml-1">
                            <i className="fa fa-check-circle text-green-600 text-xs sm:text-sm"></i>
                        </div>
                    </div>
                </div>

                {/* Processing Logs */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4">
                    <div className="flex items-center justify-between">
                        <div className="min-w-0">
                            <p className="text-xs sm:text-sm font-medium text-yellow-600 truncate">Processing Logs</p>
                            <p className="text-xl sm:text-2xl font-bold text-yellow-800">{summaryStats.processingLogs}</p>
                        </div>
                        <div className="w-7 h-7 sm:w-8 sm:h-8 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0 ml-1">
                            <i className="fa fa-clock text-yellow-600 text-xs sm:text-sm"></i>
                        </div>
                    </div>
                </div>

                {/* Posted Logs */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4">
                    <div className="flex items-center justify-between">
                        <div className="min-w-0">
                            <p className="text-xs sm:text-sm font-medium text-green-600 truncate">Posted Logs</p>
                            <p className="text-xl sm:text-2xl font-bold text-green-800">{summaryStats.postedLogs}</p>
                        </div>
                        <div className="w-7 h-7 sm:w-8 sm:h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 ml-1">
                            <i className="fa fa-check-circle text-green-600 text-xs sm:text-sm"></i>
                        </div>
                    </div>
                </div>

                <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4">
                    <div className="flex items-center justify-between">
                        <div className="min-w-0">
                            <p className="text-xs sm:text-sm font-medium text-red-600 truncate">Failed Logs</p>
                            <p className="text-xl sm:text-2xl font-bold text-red-800">{summaryStats.failedLogs}</p>
                        </div>
                        <div className="w-7 h-7 sm:w-8 sm:h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 ml-1">
                            <i className="fa fa-times-circle text-red-600 text-xs sm:text-sm"></i>
                        </div>
                    </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4">
                    <div className="flex items-center justify-between">
                        <div className="min-w-0">
                            <p className="text-xs sm:text-sm font-medium text-yellow-600 truncate">Total Amount</p>
                            <p className="text-base sm:text-lg font-bold text-yellow-800 break-all">{formatToPKR(summaryStats.totalAmount)}</p>
                        </div>
                        <div className="w-7 h-7 sm:w-8 sm:h-8 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0 ml-1">
                            <span className="text-yellow-600 font-semibold text-xs sm:text-sm">Rs</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Header Section with Search and Export */}
            <div className="flex flex-row gap-2 items-center px-3">
                {/* Table Search Filter — fills available space */}
                <div className="flex-1 min-w-0">
                    <TableSearchFilter
                        setSearchCriteria={setSearchCriteria as any}
                        inputValue={inputValue}
                        setInputValue={setInputValue}
                        placeHolder="Search by txn or bucket no."
                        initialValue={initialValue as any}
                    />
                </div>

                {/* Export + Filter — always same row */}
                <div className="flex gap-2 flex-shrink-0">
                    <Button
                        onClick={handleExportSelected}
                        disabled={isExporting}
                        className="bg-primary text-sm text-black flex items-center gap-1 h-8 w-8 p-0 sm:w-auto sm:px-2 flex-shrink-0"
                    >
                        <FaFileCsv className="size-3.5 text-black flex-shrink-0" />
                        <span className="hidden sm:inline">Export CSV</span>
                    </Button>

                    <button
                        className={`border rounded-full size-3 text-gray-400 p-4 flex items-center justify-center flex-shrink-0 ${Object.keys(filterData).length ? "border-primary" : ""}`}
                        onClick={() => setOpenFilter(true)}
                    >
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <i className={`fa fa-filter ${Object.keys(filterData).length ? "text-primary" : ""}`}></i>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>click to apply filter</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </button>
                </div>
            </div>

            {/* Gmail-style Select All Notification */}
            {(showSelectAllNotification || selectAllRows) && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-2 sm:p-3 mx-3 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                    <div className="text-xs sm:text-sm text-blue-800">
                        {selectAllRows ? (
                            <>
                                All <strong>{totalRecords}</strong> ERP logs are selected.
                            </>
                        ) : (
                            <>
                                All <strong>{table.getSelectedRowModel().rows.length}</strong> ERP logs on this page are selected.
                            </>
                        )}
                    </div>
                    {!selectAllRows && (
                        <button
                            onClick={handleSelectAllRows}
                            className="text-blue-600 hover:text-blue-800 underline text-xs sm:text-sm font-medium"
                        >
                            Select all {totalRecords} ERP logs
                        </button>
                    )}
                    {selectAllRows && (
                        <button
                            onClick={handleClearSelection}
                            className="text-blue-600 hover:text-blue-800 underline text-xs sm:text-sm font-medium"
                        >
                            Clear selection
                        </button>
                    )}
                </div>
            )}

            {/* Table */}
            <div className="rounded-none border border-border">
                <ScrollArea className="w-full relative" hideScrollToTop={true}>
                    <ScrollBar
                        orientation="horizontal"
                        className="relative z-30 cursor-grab h-2"
                    />
                    <Table className="w-full sm:max-h-[40vh] lg:max-h-[50vh] xl:max-h-[60vh] 2xl:max-h-[65vh]">
                        <TableHeader className="bg-secondary">
                            {table.getHeaderGroups().map((headerGroup) => (
                                <TableRow key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => {
                                        return (
                                            <TableHead key={header.id}>
                                                {header.isPlaceholder
                                                    ? null
                                                    : flexRender(
                                                        header.column.columnDef.header,
                                                        header.getContext()
                                                    )}
                                            </TableHead>
                                        )
                                    })}
                                </TableRow>
                            ))}
                        </TableHeader>
                        <TableBody>
                            {table.getRowModel().rows.length ? (
                                table.getRowModel().rows.map((row) => (
                                    <TableRow
                                        key={row.id}
                                        data-state={row.getIsSelected() && "selected"}
                                        className="hover:bg-secondary"
                                    >
                                        {row.getVisibleCells().map((cell) => (
                                            <TableCell key={cell.id}>
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext()
                                                )}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={columns.length} className="h-24 text-center">
                                        {isLoading ? (
                                            <div className="flex items-center justify-center gap-2">
                                                <i className="fa fa-spinner fa-spin"></i>
                                                <span>Loading ERP logs...</span>
                                            </div>
                                        ) : isError ? (
                                            'Error loading ERP logs. Please try again.'
                                        ) : searchCriteria.search_key ? (
                                            'No ERP logs found matching your search'
                                        ) : (
                                            'No ERP logs available'
                                        )}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </ScrollArea>
            </div>

            {/* Pagination */}
            {tableData.length > 0 && (
                <Pagination
                    limit={typeof searchCriteria.limit === 'number' ? searchCriteria.limit : 10}
                    offset={typeof searchCriteria.offset === 'number' ? searchCriteria.offset : 0}
                    totalItems={totalRecords}
                    onLimitChange={handleLimitChange}
                    onNextPage={handleNextPage}
                    onPrevPage={handlePrevPage}
                    onFirstPage={handleFirstPage}
                    onLastPage={handleLastPage}
                    isLastPage={isLastPage}
                />
            )}

            {/* Details Dialog */}
            <AlertDialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
                <AlertDialogContent className="max-w-2xl w-[95vw] sm:w-auto">
                    <AlertDialogHeader>
                        <AlertDialogTitle>ERP Log Details</AlertDialogTitle>
                        <AlertDialogDescription>
                            {selectedLog && (
                                <div className="space-y-4 py-4">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                        <div>
                                            <p className="text-sm font-semibold text-gray-700">Tax No.</p>
                                            <p className="text-sm text-gray-900">{selectedLog.transaction_tax_number || '-'}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm font-semibold text-gray-700">Transaction ID</p>
                                            <p className="text-sm text-gray-900">{selectedLog.transaction_id}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm font-semibold text-gray-700">Amount</p>
                                            <p className="text-sm text-gray-900">{formatToPKR(selectedLog.amount)}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm font-semibold text-gray-700">Status</p>
                                            <div className="mt-1">{getStatusBadge(selectedLog.status)}</div>
                                        </div>
                                        <div>
                                            <p className="text-sm font-semibold text-gray-700">Created Date</p>
                                            <p className="text-sm text-gray-900">{displayDateTime(selectedLog.created_at)}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm font-semibold text-gray-700">Response Code</p>
                                            <p className="text-sm text-gray-900">{selectedLog.response_code || '-'}</p>
                                        </div>
                                    </div>
                                    {selectedLog.message && (
                                        <div className="pt-4 border-t">
                                            <p className="text-sm font-semibold text-gray-700">Message</p>
                                            <p className={cn(
                                                "text-sm mt-1 p-3 rounded-md break-words",
                                                selectedLog.status.toLowerCase() === 'failed'
                                                    ? "text-red-600 bg-red-50"
                                                    : "text-gray-600 bg-gray-50"
                                            )}>
                                                {selectedLog.message}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="flex justify-end gap-3">
                        <AlertDialogCancel onClick={() => setShowDetailsDialog(false)}>
                            Close
                        </AlertDialogCancel>
                    </div>
                </AlertDialogContent>
            </AlertDialog>

            {/* Table Filters */}
            <TableFilters
                isOpen={openFilter}
                setOpen={setOpenFilter}
                initialValue={initialValue}
                filterData={filterData}
                setFilter={setFilter}
                setSearchCriteria={setSearchCriteria}
                filterDisplay={filterDisplay}
                setInputValue={setInputValue}
            />
        </div>
    )
}

